import React, { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useBusiness } from '../context/BusinessContext';
import { useModal } from '../context/ModalContext';
import DashboardLayout from '../components/DashboardLayout';
import BookingsViewToggle from '../components/BookingsViewToggle';
import { Reservation } from '../types';
import { BOOKING_STATUS, PAYMENT_FLOW } from '../constants/checkout';
import { parseBookingDate } from '../utils/bookingDate';

// ============================================
// Constants & Helpers
// ============================================

const SOURCE_LABELS: Record<string, string> = {
  app: 'App',
  manual: 'Manual',
  phone: 'Phone',
  walkin: 'Walk-in',
};

const COURT_COLORS = [
  { bg: '#e6f2e9', border: '#236332', text: '#1a4d26' },
  { bg: '#e8edfc', border: '#2f58dd', text: '#2445b3' },
  { bg: '#fcf0e9', border: '#dd7d47', text: '#c46835' },
  { bg: '#f9fce6', border: '#b8c410', text: '#8a9a0d' },
  { bg: '#fce6f2', border: '#dd47a0', text: '#b83580' },
  { bg: '#e6f9fc', border: '#47c4dd', text: '#35a0b8' },
  { bg: '#f2e6fc', border: '#9447dd', text: '#7535b8' },
  { bg: '#fcf6e6', border: '#ddaa47', text: '#b88935' },
];

const STATUS_CONFIG: Record<string, { bg: string; text: string; label: string }> = {
  manual_booking_approval: { bg: '#dbeafe', text: '#1d4ed8', label: 'Manual Approval Queue' },
  pending_approval: { bg: '#fef3c7', text: '#92400e', label: 'Awaiting Approval' },
  approved: { bg: '#dcfce7', text: '#166534', label: 'Approved' },
  declined: { bg: '#fee2e2', text: '#991b1b', label: 'Declined' },
  expired: { bg: '#f3f4f6', text: '#6b7280', label: 'Expired' },
  confirmed: { bg: '#dcfce7', text: '#166534', label: 'Confirmed' },
  pending: { bg: '#fef9c3', text: '#854d0e', label: 'Pending' },
  canceled: { bg: '#f3f4f6', text: '#6b7280', label: 'Canceled' },
  completed: { bg: '#f3f4f6', text: '#6b7280', label: 'Completed' },
};

function getDisplayStatus(booking: Reservation): { bg: string; text: string; label: string; pulse?: boolean } {
  // Auto-approval in progress
  if (booking.status === 'pending_approval' && booking.autoApprovalStatus === 'queued') {
    return { bg: '#dbeafe', text: '#1e40af', label: 'Auto-Approving...', pulse: true };
  }
  // Auto-cancellation in progress
  if (booking.status === 'approved' && booking.autoCancellationStatus === 'queued') {
    return { bg: '#fef3c7', text: '#92400e', label: 'Canceling...', pulse: true };
  }
  if (booking.status === 'canceled' && booking.autoCancellationStatus === 'queued') {
    return { bg: '#fef3c7', text: '#92400e', label: 'Canceling on CR...', pulse: true };
  }
  // External-hold (manual) bookings are the human-books-externally queue. They
  // now share the 'pending_approval' status with deferred flows, so discriminate
  // on payment_flow to keep the distinct Manual Approval Queue badge.
  if (booking.status === BOOKING_STATUS.PENDING_APPROVAL && booking.paymentFlow === PAYMENT_FLOW.EXTERNAL_HOLD) {
    return STATUS_CONFIG.manual_booking_approval;
  }
  return STATUS_CONFIG[booking.status] || STATUS_CONFIG.confirmed;
}

function formatTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${hr} ${suffix}` : `${hr}:${m.toString().padStart(2, '0')} ${suffix}`;
}

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatFullDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCreatedAt(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function getTodayStr(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function dateToStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function reservationDateTime(reservation: Reservation, value: 'start' | 'end'): Date {
  return new Date(`${reservation.date}T${value === 'start' ? reservation.startTime : reservation.endTime}:00`);
}

/** Check if a booking's time slot has already passed (for today only) */
function isBookingPast(booking: Reservation, todayStr: string): boolean {
  if (booking.date !== todayStr) return false;
  const now = new Date();
  const [endH, endM] = booking.endTime.split(':').map(Number);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const endMinutes = endH * 60 + endM;
  return endMinutes <= nowMinutes;
}

// ============================================
// Main Component
// ============================================

export default function BookingsScreen() {
  const { business, reservations, updateReservation } = useBusiness();
  const { showAlert, showDanger, showConfirm } = useModal();
  const [searchParams] = useSearchParams();

  type BookingScope = 'selected_day' | 'upcoming' | 'past' | 'all';
  type SortField = 'date_time' | 'created_at' | 'customer' | 'court' | 'status';

  const [search, setSearch] = useState('');
  const [selectedBooking, setSelectedBooking] = useState<Reservation | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [pendingExpanded, setPendingExpanded] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>(() => parseBookingDate(searchParams.get('date')) || new Date());
  const [scope, setScope] = useState<BookingScope>('selected_day');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [courtFilter, setCourtFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('date_time');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const activeCourts = useMemo(
    () => business?.courts.filter(c => c.isActive).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })) || [],
    [business]
  );

  const getCourtIndex = useCallback(
    (courtId: string) => activeCourts.findIndex(c => c.id === courtId),
    [activeCourts]
  );

  const getCourtPillStyle = useCallback((courtId: string): React.CSSProperties => {
    const idx = getCourtIndex(courtId);
    const color = idx >= 0 ? COURT_COLORS[idx % COURT_COLORS.length] : { bg: '#f3f4f6', border: '#9ca3af', text: '#4b5563' };
    return {
      fontSize: '11px',
      fontWeight: 700,
      color: color.text,
      background: color.bg,
      border: `1px solid ${color.border}`,
      borderRadius: '999px',
      padding: '2px 8px',
      lineHeight: 1.2,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
    };
  }, [getCourtIndex]);

  const getCourtName = useCallback(
    (courtId: string) => activeCourts.find(c => c.id === courtId)?.name || 'Unknown Court',
    [activeCourts]
  );

  const todayStr = getTodayStr();
  const selectedDateStr = dateToStr(selectedDate);

  const statusOptions = useMemo(
    () => Array.from(new Set(reservations.map(r => r.status))).sort(),
    [reservations]
  );

  const matchesCommonFilters = useCallback((booking: Reservation): boolean => {
    const q = search.toLowerCase().trim();
    if (q.length > 0) {
      const courtName = getCourtName(booking.courtId).toLowerCase();
      const searchHit = booking.customerName.toLowerCase().includes(q)
        || (booking.confirmationCode?.toLowerCase().includes(q))
        || (booking.customerEmail?.toLowerCase().includes(q))
        || courtName.includes(q);
      if (!searchHit) return false;
    }

    if (courtFilter !== 'all' && booking.courtId !== courtFilter) return false;
    if (statusFilter !== 'all' && booking.status !== statusFilter) return false;

    return true;
  }, [search, getCourtName, courtFilter, statusFilter]);

  // Pending approval bookings (always shown at top regardless of date)
  const pendingApproval = useMemo(() => {
    return reservations
      .filter(matchesCommonFilters)
      .filter(r => r.status === BOOKING_STATUS.PENDING_APPROVAL || r.status === 'pending' || r.status === 'manual_booking_approval')
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }, [reservations, matchesCommonFilters]);

  // Bookings explorer list with scope/filter/sort controls.
  const explorerReservations = useMemo(() => {
    const now = new Date();

    const scoped = reservations
      .filter(matchesCommonFilters)
      .filter(r => {
        if (scope === 'selected_day') return r.date === selectedDateStr;
        if (scope === 'upcoming') return reservationDateTime(r, 'end') >= now;
        if (scope === 'past') return reservationDateTime(r, 'end') < now;
        return true;
      });

    scoped.sort((a, b) => {
      let compare = 0;
      if (sortField === 'date_time') {
        compare = reservationDateTime(a, 'start').getTime() - reservationDateTime(b, 'start').getTime();
      } else if (sortField === 'created_at') {
        compare = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      } else if (sortField === 'customer') {
        compare = a.customerName.localeCompare(b.customerName);
      } else if (sortField === 'court') {
        compare = getCourtName(a.courtId).localeCompare(getCourtName(b.courtId), undefined, { numeric: true });
      } else if (sortField === 'status') {
        compare = (STATUS_CONFIG[a.status]?.label || a.status).localeCompare(STATUS_CONFIG[b.status]?.label || b.status);
      }
      return sortDirection === 'asc' ? compare : -compare;
    });

    return scoped;
  }, [reservations, matchesCommonFilters, scope, selectedDateStr, sortField, sortDirection, getCourtName]);

  // Date navigation
  const navigateDay = (direction: number) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const goToToday = () => setSelectedDate(new Date());

  const toggleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
      return;
    }

    setSortField(field);
    setSortDirection(field === 'created_at' ? 'desc' : 'asc');
  }, [sortField]);

  // Actions
  const handleApprove = async (booking: Reservation) => {
    try {
      await updateReservation(booking.id, { status: 'confirmed' });
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to approve', 'error');
    }
  };

  const handleReject = async (booking: Reservation) => {
    const confirmed = await showConfirm(
      'Reject Booking',
      `Reject booking for ${booking.customerName}?`,
      { confirmLabel: 'Reject', type: 'warning' }
    );
    if (confirmed) {
      try {
        await updateReservation(booking.id, { status: 'canceled' });
      } catch (err) {
        await showAlert('Error', err instanceof Error ? err.message : 'Failed to reject', 'error');
      }
    }
  };

  const handleCancel = async (booking: Reservation) => {
    const confirmed = await showConfirm(
      'Cancel Reservation',
      `Cancel reservation for ${booking.customerName}?`,
      { confirmLabel: 'Yes, Cancel', type: 'warning' }
    );
    if (confirmed) {
      try {
        await updateReservation(booking.id, { status: 'canceled' });
        setShowDetailModal(false);
      } catch (err) {
        await showAlert('Error', err instanceof Error ? err.message : 'Failed to cancel', 'error');
      }
    }
  };


  const openDetail = (booking: Reservation) => {
    setSelectedBooking(booking);
    setShowDetailModal(true);
  };

  const isViewingToday = selectedDateStr === todayStr;

  return (
    <DashboardLayout>
      <div className="page-header bookings-page-heading">
        <div>
          <h1>Booking management</h1>
          <p>Process PickleCourts requests and search, filter, or review booking records.</p>
        </div>
        <BookingsViewToggle selectedDate={selectedDateStr} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

        {/* ── Pending Approval ── */}
        {pendingApproval.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: 'hidden', border: '1px solid #fde68a' }}>
            <div
              onClick={() => setPendingExpanded(!pendingExpanded)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px',
                cursor: 'pointer', borderBottom: pendingExpanded ? '1px solid #e5e7eb' : 'none',
                background: '#fffbeb',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#92400e' }}>Pending Approval</span>
              <span style={{
                fontSize: '12px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px',
                background: 'rgba(0,0,0,0.06)', color: '#92400e',
              }}>
                {pendingApproval.length}
              </span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2" style={{ marginLeft: 'auto', transform: pendingExpanded ? 'rotate(0)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            {pendingExpanded && (
              <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {pendingApproval.map(booking => (
                  <div
                    key={booking.id}
                    className="bookings-approval-card"
                    onClick={() => openDetail(booking)}
                    style={{ borderRadius: '10px', border: '1px solid #fde68a', background: '#fff', overflow: 'hidden', cursor: 'pointer', transition: 'all 0.15s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    <div style={{ height: '3px', background: 'linear-gradient(90deg, #f59e0b, #fbbf24)' }} />
                    <div style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Name + time ago */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827', letterSpacing: '-0.01em' }}>{booking.customerName}</span>
                            <span style={{ fontSize: '10px', color: '#b0b5be', flexShrink: 0 }}>{timeAgo(booking.createdAt)}</span>
                          </div>

                          {/* Contact details */}
                          {(booking.customerEmail || booking.customerPhone) && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginBottom: '8px' }}>
                              {booking.customerEmail && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ flexShrink: 0 }}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                                  <span style={{ fontSize: '12px', color: '#6b7280' }}>{booking.customerEmail}</span>
                                </div>
                              )}
                              {booking.customerPhone && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                                  <span style={{ fontSize: '12px', color: '#6b7280' }}>{booking.customerPhone}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Booking details row */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <span style={getCourtPillStyle(booking.courtId)}>{getCourtName(booking.courtId)}</span>
                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                              {formatDate(booking.date)} &middot; {formatTime(booking.startTime)} – {formatTime(booking.endTime)}
                            </span>
                            {booking.confirmationCode && (
                              <span style={{ fontSize: '10px', color: '#b0b5be', fontFamily: 'monospace', marginLeft: 'auto' }}>{booking.confirmationCode}</span>
                            )}
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: '8px', flexShrink: 0, paddingTop: '2px' }} onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => handleApprove(booking)}
                            style={{
                              padding: '7px 18px', fontSize: '12px', fontWeight: 600,
                              background: '#166534', color: '#fff', border: 'none', borderRadius: '8px',
                              cursor: 'pointer', transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#14532d')}
                            onMouseLeave={e => (e.currentTarget.style.background = '#166534')}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(booking)}
                            style={{
                              padding: '7px 14px', fontSize: '12px', fontWeight: 500,
                              background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
                              color: '#6b7280', cursor: 'pointer', transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#374151'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Bookings Explorer ── */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {/* Booking scope and selected-day controls */}
          <div className="booking-explorer-bar">
            <div className="booking-scope-switch" role="group" aria-label="Bookings time range">
              {([
                { value: 'selected_day', label: 'Day' },
                { value: 'upcoming', label: 'Upcoming' },
                { value: 'past', label: 'Past' },
                { value: 'all', label: 'All' },
              ] as const).map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={`booking-scope-switch__item ${scope === option.value ? 'booking-scope-switch__item--active' : ''}`}
                  onClick={() => setScope(option.value)}
                  aria-pressed={scope === option.value}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {scope === 'selected_day' && (
              <div className="booking-date-control">
                <button type="button" onClick={() => navigateDay(-1)} aria-label="Previous day">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                </button>

                <span>{formatDate(selectedDateStr)}</span>

                <button type="button" onClick={() => navigateDay(1)} aria-label="Next day">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                </button>

                <label className="booking-date-control__picker" aria-label="Choose date">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <input
                    type="date"
                    value={selectedDateStr}
                    onChange={e => {
                      const parts = e.target.value.split('-').map(Number);
                      if (parts.length === 3) setSelectedDate(new Date(parts[0], parts[1] - 1, parts[2]));
                    }}
                  />
                </label>

                {!isViewingToday && (
                  <button type="button" className="booking-date-control__today" onClick={goToToday}>Today</button>
                )}
              </div>
            )}

            <span className="booking-explorer-bar__count">
              {explorerReservations.length} booking{explorerReservations.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Filters Toolbar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px',
            borderBottom: '1px solid #e5e7eb', background: '#fff', flexWrap: 'wrap',
          }}>
            <div style={{ position: 'relative', flex: '1 1 180px', maxWidth: '240px' }}>
              <svg style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', width: '12px', height: '12px', color: '#9ca3af' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input
                type="text"
                placeholder="Search name, email, code..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  width: '100%', padding: '5px 8px 5px 26px', border: '1px solid #e5e7eb',
                  borderRadius: '5px', fontSize: '12px', outline: 'none',
                }}
              />
            </div>
            <select value={courtFilter} onChange={e => setCourtFilter(e.target.value)} style={{ padding: '5px 6px', border: '1px solid #e5e7eb', borderRadius: '5px', fontSize: '11px', color: '#374151' }}>
              <option value="all">All Courts</option>
              {activeCourts.map(court => (
                <option key={court.id} value={court.id}>{court.name}</option>
              ))}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: '5px 6px', border: '1px solid #e5e7eb', borderRadius: '5px', fontSize: '11px', color: '#374151' }}>
              <option value="all">All Statuses</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>{STATUS_CONFIG[status]?.label || status}</option>
              ))}
            </select>
            {(search || scope !== 'selected_day' || courtFilter !== 'all' || statusFilter !== 'all' || sortField !== 'date_time' || sortDirection !== 'asc') && (
              <button
                onClick={() => {
                  setSearch('');
                  setScope('selected_day');
                  setStatusFilter('all');
                  setCourtFilter('all');
                  setSortField('date_time');
                  setSortDirection('asc');
                }}
                style={{
                  padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: '5px',
                  background: 'none', cursor: 'pointer', fontSize: '11px', color: '#6b7280',
                  fontWeight: 500,
                }}
              >
                Reset
              </button>
            )}
          </div>

          {/* Column Headers */}
          <div style={{
            display: 'flex', alignItems: 'center', padding: '6px 16px', gap: '8px',
            borderBottom: '1px solid #e5e7eb', background: '#f9fafb',
            fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            <button
              onClick={() => toggleSort('date_time')}
              style={{ width: '150px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: sortField === 'date_time' ? '#111827' : '#6b7280' }}
              title="Sort by booking time"
            >
              Time {sortField === 'date_time' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
            </button>
            <button
              onClick={() => toggleSort('court')}
              style={{ width: '100px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: sortField === 'court' ? '#111827' : '#6b7280' }}
              title="Sort by court"
            >
              Court {sortField === 'court' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
            </button>
            <button
              onClick={() => toggleSort('customer')}
              style={{ flex: 1, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: sortField === 'customer' ? '#111827' : '#6b7280' }}
              title="Sort by customer"
            >
              Customer {sortField === 'customer' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
            </button>
            <button
              onClick={() => toggleSort('created_at')}
              style={{ width: '170px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: sortField === 'created_at' ? '#111827' : '#6b7280' }}
              title="Sort by created time"
            >
              Created {sortField === 'created_at' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
            </button>
            <button
              onClick={() => toggleSort('status')}
              style={{ width: '110px', background: 'none', border: 'none', textAlign: 'right', cursor: 'pointer', fontSize: '11px', fontWeight: 600, color: sortField === 'status' ? '#111827' : '#6b7280' }}
              title="Sort by status"
            >
              Status {sortField === 'status' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
            </button>
          </div>

          {/* Reservation Rows */}
          <div style={{ maxHeight: '600px', overflow: 'auto' }}>
            {explorerReservations.length === 0 ? (
              <div style={{ padding: '40px 16px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                {scope === 'selected_day' && !search && courtFilter === 'all' && statusFilter === 'all'
                  ? 'No PickleCourts bookings for this date'
                  : 'No PickleCourts bookings match your filters'}
              </div>
            ) : (
              explorerReservations.map(booking => {
                const sc = getDisplayStatus(booking);
                const isPast = isBookingPast(booking, todayStr);
                return (
                  <div
                    key={booking.id}
                    onClick={() => openDetail(booking)}
                    className="bookings-row"
                    style={{
                      opacity: isPast ? 0.45 : 1,
                    }}
                  >
                    <span className="bookings-row__cell" style={{ width: '150px', fontWeight: 500 }}>
                      {formatTime(booking.startTime)} – {formatTime(booking.endTime)}
                    </span>
                    <span className="bookings-row__cell" style={{ width: '100px', color: '#6b7280', fontSize: '12px' }}>
                      <span style={getCourtPillStyle(booking.courtId)}>
                        {getCourtName(booking.courtId)}
                      </span>
                    </span>
                    <span className="bookings-row__cell" style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                        {booking.customerName}
                      </span>
                      {(booking.customerEmail || booking.customerPhone) && (
                        <span style={{ fontSize: '11px', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                          {booking.customerEmail && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#c0c4cc" strokeWidth="2" style={{ flexShrink: 0 }}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                              {booking.customerEmail}
                            </span>
                          )}
                          {booking.customerPhone && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#c0c4cc" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                              {booking.customerPhone}
                            </span>
                          )}
                        </span>
                      )}
                    </span>
                    <span className="bookings-row__cell" style={{ width: '170px', color: '#4b5563', fontSize: '12px' }}>
                      <span style={{ display: 'block', fontWeight: 500 }}>{formatCreatedAt(booking.createdAt)}</span>
                      <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af' }}>{timeAgo(booking.createdAt)}</span>
                    </span>
                    <span style={{
                      width: '110px', textAlign: 'right', flexShrink: 0, display: 'flex', justifyContent: 'flex-end',
                    }}>
                      <span style={{
                        fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '4px',
                        background: sc.bg, color: sc.text, whiteSpace: 'nowrap',
                        ...('pulse' in sc && sc.pulse ? { animation: 'pulse-status 1.5s ease-in-out infinite' } : {}),
                      }}>
                        {sc.label}
                      </span>
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* ── Detail Modal ── */}
      {showDetailModal && selectedBooking && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h2>Booking Details</h2>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {selectedBooking.confirmationCode && (
                <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px', marginBottom: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px' }}>Confirmation Code</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#111827', fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                    {selectedBooking.confirmationCode}
                  </div>
                </div>
              )}

              {(() => {
                const sc = getDisplayStatus(selectedBooking);
                return (
                  <div style={{ marginBottom: '16px' }}>
                    <span style={{
                      fontSize: '12px', fontWeight: 600, padding: '4px 12px', borderRadius: '6px',
                      background: sc.bg, color: sc.text,
                      ...('pulse' in sc && sc.pulse ? { animation: 'pulse-status 1.5s ease-in-out infinite' } : {}),
                    }}>
                      {sc.label}
                    </span>
                  </div>
                );
              })()}

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>Customer</div>
                <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827', marginBottom: '8px', letterSpacing: '-0.01em' }}>{selectedBooking.customerName}</div>
                {(selectedBooking.customerEmail || selectedBooking.customerPhone) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px 12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #f3f4f6' }}>
                    {selectedBooking.customerEmail && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ flexShrink: 0 }}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                        <a href={`mailto:${selectedBooking.customerEmail}`} onClick={e => e.stopPropagation()} style={{ fontSize: '13px', color: '#2563eb', textDecoration: 'none' }}
                          onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                          onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                        >{selectedBooking.customerEmail}</a>
                      </div>
                    )}
                    {selectedBooking.customerPhone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                        <a href={`tel:${selectedBooking.customerPhone}`} onClick={e => e.stopPropagation()} style={{ fontSize: '13px', color: '#2563eb', textDecoration: 'none' }}
                          onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                          onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                        >{selectedBooking.customerPhone}</a>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '8px' }}>Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '6px 12px', fontSize: '13px' }}>
                  <span style={{ color: '#9ca3af' }}>Court</span>
                  <span style={{ color: '#111827', fontWeight: 500 }}>{getCourtName(selectedBooking.courtId)}</span>
                  <span style={{ color: '#9ca3af' }}>Date</span>
                  <span style={{ color: '#111827' }}>{formatFullDate(selectedBooking.date)}</span>
                  <span style={{ color: '#9ca3af' }}>Time</span>
                  <span style={{ color: '#111827' }}>{formatTime(selectedBooking.startTime)} - {formatTime(selectedBooking.endTime)}</span>
                  <span style={{ color: '#9ca3af' }}>Duration</span>
                  <span style={{ color: '#111827' }}>{selectedBooking.durationHours}h</span>
                  <span style={{ color: '#9ca3af' }}>Source</span>
                  <span style={{ color: '#111827' }}>{SOURCE_LABELS[selectedBooking.source] || selectedBooking.source}</span>
                  <span style={{ color: '#9ca3af' }}>Booked</span>
                  <span style={{ color: '#111827' }}>{new Date(selectedBooking.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
              </div>

              {selectedBooking.notes && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '8px' }}>Notes</div>
                  <div style={{ fontSize: '13px', color: '#374151', background: '#f9fafb', padding: '8px 12px', borderRadius: '6px' }}>
                    {selectedBooking.notes}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {selectedBooking.status === 'pending_approval' && (
                <>
                  <button className="btn btn-danger" onClick={() => handleCancel(selectedBooking)}>Cancel</button>
                  <button className="btn btn-secondary" onClick={() => handleReject(selectedBooking)}>Reject</button>
                  <button className="btn btn-primary" onClick={() => { handleApprove(selectedBooking); setShowDetailModal(false); }}>Approve</button>
                </>
              )}
              {(selectedBooking.status === 'approved' || selectedBooking.status === 'confirmed' || selectedBooking.status === 'pending') && (
                <button className="btn btn-danger" onClick={() => handleCancel(selectedBooking)}>Cancel Reservation</button>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
